import { useState } from 'react';
import { writeTestPing } from '../utils/testFirebase';
import { useAuth } from '../contexts/AuthContext';

export default function FirebaseTest() {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const runTests = async () => {
      setLoading(true);
      
      // Test project configuration
      const configResults = verifyProjectConfig();
      setProjectConfig(configResults);
      
      // Test Firebase connection
      const connectionResults = await testFirebaseConnection();
      setTestResults(connectionResults);
      
      setLoading(false);
    };

    runTests();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Testing Firebase connection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">🔧 Firebase Configuration Test</h1>
          
          {/* Project Configuration */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">📋 Project Configuration</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p><strong>Project ID:</strong> {projectConfig?.projectId}</p>
                  <p><strong>Auth Domain:</strong> {projectConfig?.authDomain}</p>
                  <p><strong>Is ExamLock Project:</strong> 
                    <span className={projectConfig?.isExamLockProject ? 'text-green-600' : 'text-red-600'}>
                      {projectConfig?.isExamLockProject ? ' ✅ Yes' : ' ❌ No'}
                    </span>
                  </p>
                </div>
                <div>
                  <p><strong>Expected Project:</strong> {projectConfig?.expectedProject}</p>
                  <p><strong>Config Complete:</strong> 
                    <span className={projectConfig?.configComplete ? 'text-green-600' : 'text-red-600'}>
                      {projectConfig?.configComplete ? ' ✅ Yes' : ' ❌ No'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Connection Status */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">🔗 Connection Status</h2>
            
            {/* Firebase Config */}
            <div className="mb-4 p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-600 mb-2">Firebase Configuration</h3>
              <div className="text-sm">
                <p><strong>Status:</strong> 
                  <span className="text-green-600"> ✅ {testResults?.config?.status}</span>
                </p>
                <p><strong>Project ID:</strong> {testResults?.config?.projectId}</p>
                <p><strong>Auth Domain:</strong> {testResults?.config?.authDomain}</p>
              </div>
            </div>

            {/* Auth Status */}
            <div className="mb-4 p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-600 mb-2">Authentication Service</h3>
              <div className="text-sm">
                <p><strong>Status:</strong> 
                  <span className={testResults?.auth?.status === 'Connected' ? 'text-green-600' : 'text-red-600'}>
                    {testResults?.auth?.status === 'Connected' ? ' ✅' : ' ❌'} {testResults?.auth?.status}
                  </span>
                </p>
                <p><strong>Current User:</strong> {testResults?.auth?.currentUser}</p>
                {testResults?.auth?.error && (
                  <p className="text-red-600"><strong>Error:</strong> {testResults.auth.error}</p>
                )}
              </div>
            </div>

            {/* Firestore Status */}
            <div className="mb-4 p-4 border rounded-lg">
              <h3 className="font-semibold text-gray-600 mb-2">Firestore Database</h3>
              <div className="text-sm">
                <p><strong>Status:</strong> 
                  <span className={testResults?.firestore?.status === 'Connected' ? 'text-green-600' : 'text-red-600'}>
                    {testResults?.firestore?.status === 'Connected' ? ' ✅' : ' ❌'} {testResults?.firestore?.status}
                  </span>
                </p>
                <p><strong>Project ID:</strong> {testResults?.firestore?.projectId}</p>
                {testResults?.firestore?.error && (
                  <p className="text-red-600"><strong>Error:</strong> {testResults.firestore.error}</p>
                )}
              </div>
            </div>
          </div>

          {/* Current Auth Status */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">👤 Current Authentication</h2>
            <div className="bg-blue-50 rounded-lg p-4">
              <p><strong>Logged in:</strong> {user ? '✅ Yes' : '❌ No'}</p>
              {user && (
                <>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>User ID:</strong> {user.uid}</p>
                  <p><strong>Role:</strong> {userRole || 'Loading...'}</p>
                </>
              )}
            </div>
          </div>

          {/* Errors */}
          {testResults?.errors && testResults.errors.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-red-700 mb-4">❌ Errors</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                {testResults.errors.map((error, index) => (
                  <p key={index} className="text-red-700 mb-2">{error}</p>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">📊 Summary</h3>
            <p className="text-sm text-gray-600">
              {projectConfig?.isExamLockProject && testResults?.config?.status === 'Connected' && testResults?.auth?.status === 'Connected' && testResults?.firestore?.status === 'Connected'
                ? '🎉 Firebase is properly configured and connected to your ExamLock project!'
                : '⚠️ There may be configuration issues. Please check the details above.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}